// Requêtes des écrans des travaux (lot L4-C, TanStack Query), clés sous « travaux ». Types de
// packages/contrats/src/travaux.ts ; une situation de travaux passe par le contrat commun de la dépense.
import type {
  AvancementSaisi,
  CreationFinancement,
  CreationProjet,
  CreationRapport,
  CreationReception,
  CreationSituation,
  DefinitionJalons,
  EngagementDepense,
  ProjetTravaux,
  ResumeProjet,
} from '@cc/contrats';
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../api/client';

export interface Liste<T> {
  elements: T[];
}

export const INVALIDER_TRAVAUX: readonly (readonly unknown[])[] = [['travaux'], ['achats'], ['depense'], ['indicateurs']];

const lire = <T,>(chemin: string, parametres: Record<string, string | undefined> = {}) => ({ signal }: { signal: AbortSignal }) => requete<T>(chemin, { signal, parametres });
const envoyer = <T,>(methode: 'POST' | 'PUT' | 'DELETE', chemin: string, corps?: unknown) => requete<T>(chemin, { methode, corps });

export const useProjets = () => useQuery({ queryKey: ['travaux', 'projets'], queryFn: lire<Liste<ResumeProjet>>('/travaux/projets'), retry: false });
export const useProjet = (id: string | undefined) => useQuery({ queryKey: ['travaux', 'projet', id ?? ''], queryFn: lire<ProjetTravaux>(`/travaux/projets/${id}`), enabled: Boolean(id), retry: false });
/** Engagements en cours (chaîne de la dépense) proposés pour une situation de travaux. */
export const useEngagementsEnCours = (actif: boolean) =>
  useQuery({ queryKey: ['depense', 'engagements', 'engage'], queryFn: lire<Liste<EngagementDepense>>('/depense/engagements', { etat: 'engage' }), enabled: actif, retry: false });

export const creerProjet = (corps: CreationProjet) => envoyer<ProjetTravaux>('POST', '/travaux/projets', corps);
export const ajouterFinancement = (id: string, corps: CreationFinancement) => envoyer<ProjetTravaux>('POST', `/travaux/projets/${id}/financements`, corps);
export const lierContrat = (id: string, contratId: string) => envoyer<ProjetTravaux>('POST', `/travaux/projets/${id}/contrats`, { contrat_id: contratId });
export const definirJalons = (id: string, corps: DefinitionJalons) => envoyer<ProjetTravaux>('PUT', `/travaux/projets/${id}/jalons`, corps);
export const lancerProjet = (id: string) => envoyer<ProjetTravaux>('POST', `/travaux/projets/${id}/lancement`, {});
export const declarerAtteint = (jalonId: string) => envoyer<ProjetTravaux>('POST', `/travaux/jalons/${jalonId}/atteint`, {});
export const validerJalon = (jalonId: string) => envoyer<ProjetTravaux>('POST', `/travaux/jalons/${jalonId}/validation`, {});
export const saisirAvancement = (id: string, corps: AvancementSaisi) => envoyer<ProjetTravaux>('POST', `/travaux/projets/${id}/avancement`, corps);
export const revenirAuCalcul = (id: string) => envoyer<ProjetTravaux>('DELETE', `/travaux/projets/${id}/avancement`);
export const creerSituation = (id: string, corps: CreationSituation) => envoyer<ProjetTravaux>('POST', `/travaux/projets/${id}/situations`, corps);
export const ajouterRapport = (id: string, corps: CreationRapport) => envoyer<ProjetTravaux>('POST', `/travaux/projets/${id}/rapports`, corps);
export const prononcerReception = (id: string, type: 'provisoire' | 'definitive', corps: CreationReception) => envoyer<ProjetTravaux>('POST', `/travaux/projets/${id}/reception-${type}`, corps);
export const leverReserve = (reserveId: string, observation: string) => envoyer<ProjetTravaux>('POST', `/travaux/reserves/${reserveId}/levee`, { observation });
export const cloturerProjet = (id: string, observation?: string) => envoyer<ProjetTravaux>('POST', `/travaux/projets/${id}/cloture`, { observation });
